import React from 'react';
import { Col, HasAccess, Row, Table, Tag, Typography } from 'components';
import { useOrderItemsByIdQuery } from 'api';
import { Link, useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { Order } from 'models/Order';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { ADMIN_ROUTES } from 'constants/routes';
import { OrderItemStatusBadge } from 'components/common/StatusBadge/OrderStatusBadge';

const { Text, Paragraph } = Typography;
export const ItemsTable = React.memo(() => {
  const { id } = useParams() as { id: string };
  const { data, isLoading } = useOrderItemsByIdQuery(id);

  const columns: ColumnsType<Order> = [
    {
      title: 'Item SKU',
      dataIndex: 'itemSku',
      key: 'itemSku',
      render: (value) => value,
    },
    {
      title: 'Item Description',
      dataIndex: 'itemDescription',
      key: 'itemDescription',
      minWidth: 200,
      render: (value: string, record: any) => (
        <Row style={{ maxWidth: 200 }}>
          <Col span={24}>
            <Text type={'secondary'}>{value}</Text>
          </Col>
          <Col span={24}>
            <Paragraph className={'reset-title'} type={'secondary'}>
              <Text type={'secondary'} className={'font-weight-700'}>
                Note:{' '}
              </Text>
              {record.notes || '-'}
            </Paragraph>
          </Col>
        </Row>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value) => value,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      minWidth: 100,
      render: (brand) => {
        return (
          <Link
            target={'_blank'}
            to={`${ADMIN_ROUTES.brand.view}/${brand.id}/management`}
            className={'underline-button'}
          >
            <Text underline type={'secondary'}>
              {brand.name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: (
        <span>
          Purchase Price <br />
          per Piece
        </span>
      ),
      dataIndex: 'purchasePricePerPiece',
      key: 'purchasePricePerPiece',
      minWidth: 100,
      render: (value) => value,
    },
    {
      title: (
        <span>
          Sell Price <br />
          per Piece
        </span>
      ),
      dataIndex: 'sellPricePerPiece',
      key: 'sellPricePerPiece',
      minWidth: 100,
      render: (value) => value,
    },
    {
      title: 'Lead Time',
      dataIndex: 'timeUnit',
      key: 'timeUnit',
      render: (value: string, record: any) => {
        const { leadTimeMax, leadTimeMin } = record;
        return (
          <Row align={'middle'} gutter={4} wrap={false}>
            <Col flex={'auto'}>
              <Text type={'secondary'} className={'no-wrap'} ellipsis={{ tooltip: true }}>
                {leadTimeMin && leadTimeMax
                  ? `${leadTimeMin}-${leadTimeMax}`
                  : leadTimeMin
                    ? `${leadTimeMin} Min`
                    : leadTimeMax
                      ? `${leadTimeMax} Max`
                      : '-'}
              </Text>
            </Col>
            {(leadTimeMin || leadTimeMax) && (
              <Col>
                <Row className={'gap-4'} justify={'center'} wrap={false}>
                  <Tag className={`tag ${value === 'DAY' && 'selected-tag'}`}>Day</Tag>
                  <Tag className={`tag ${value === 'WEEK' && 'selected-tag'} `}>Week</Tag>
                </Row>
              </Col>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <OrderItemStatusBadge type={status} />,
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value) => value,
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (value) => value,
    },
    {
      title: 'Margin (%)',
      dataIndex: 'margin',
      key: 'margin',
      render: (value) => value,
    },
  ];
  return (
    <Row className={'margin-t-30'} gutter={[0, 20]}>
      <Col>
        <Text className={'font-weight-800 font-size-18'}>Items</Text>
      </Col>
      <Col>
        <HasAccess role={PERMISSIONS.orders.order.read}>
          <Table
            tableLayout={'auto'}
            bordered
            scroll={{
              x: true,
            }}
            rowKey="id"
            size={'small'}
            dataSource={data?.items}
            columns={columns}
            loading={{
              indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
              spinning: isLoading,
            }}
            locale={
              isLoading
                ? { emptyText: null } // Hide "No Data" text while loading
                : {} // Use default AntD "No Data" icon when empty
            }
            // onChange={handleTableChange}
            pagination={false}
          />
        </HasAccess>
      </Col>
    </Row>
  );
});
